import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, Row, Skeleton, Table, Tooltip } from "antd";
import { useContext, useEffect, useState } from "react";
import { useClient } from "../../Context/ClientContext";
import { notificationContext } from "../../Common/PageRoute";
import OrganisationsService, {
  IOrganiseData,
} from "../../Services/Organisations/OrganisationsService";
import { LedgerStatus } from "../../Services/LedgerService";
import { ColumnsType } from "antd/es/table";
import { useTheme } from "../../Context/ThemeContext";
import { IIdNameModel } from "../../Services/ItemsService";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import AddOrganisationModel from "./AddOrganisationModel";
import { ConfirmationModal } from "../../Common/ConfirmationModal";

export interface OrgDataType {
  sno: number;
  id: string;
  orgName: string;
  status: LedgerStatus;
}
const Organisations = () => {
  const [openOrgModel, setOpenOrgModel] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [addUpdateTrigger, setAddUpdateTrigger] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const { themeData } = useTheme();

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const { currentClient } = useClient();
  const { openNotification } = useContext(notificationContext);
  const [orgList, setOrgList] = useState<OrgDataType[]>([]);
  const getOrgList = async () => {
    setDataLoading(true);
    await OrganisationsService.getOrganisationsList(
      currentClient?.id!,
      searchText,
      "",
      "",
      LedgerStatus.active
    ).then((val: IOrganiseData[] | null) => {
      setOrgList(
        val?.map<OrgDataType>((org, index: number) => {
          return {
            sno: index + 1,
            id: org?.id!,
            orgName: org?.orgName,
            status: org?.status!,
          };
        }) || []
      );
    });
    setDataLoading(false);
  };
  const handleDelete = async (selId: string) => {
    if (selId) {
      await OrganisationsService.deleteOrganisation(currentClient?.id!, selId)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val?.status) {
            openNotification(
              "success",
              val?.result?.name + " organisation deleted successfully"
            );
            setAddUpdateTrigger((prev) => !prev);
          } else {
            openNotification("error", val?.errors && val?.errors[0]?.message);
          }
        })
        .catch((err) => {
          openNotification("error", err?.message || "");
        });
      setDeleteConfirmation({
        open: false,
        id: "",
      });
    }
  };
  useEffect(() => {
    if (currentClient?.id) {
      getOrgList();
    }
  }, [currentClient?.id, addUpdateTrigger, searchText]);

  const orgCols: ColumnsType<OrgDataType> = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: "5%",
      render: (value: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Organisation Name",
      dataIndex: "orgName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <Row gutter={18}>
            <Col>
              <Tooltip title="Edit" placement="top" color="#3D4998">
                <EditOutlined
                  style={{
                    color: "#444653",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setOpenOrgModel({
                      open: true,
                      id: value,
                    });
                  }}
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title="Delete" placement="top" color="red">
                <DeleteOutlined
                  style={{
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setDeleteConfirmation({
                      open: true,
                      id: value,
                    })
                  }
                />
              </Tooltip>
            </Col>
          </Row>
        </Skeleton>
      ),
    },
  ];

  return (
    <Card
      size="small"
      className="TableCard"
      style={{
        marginTop: 35,
        boxShadow: "0px 0px 10px 0px #96A1C84D",
      }}
    >
      <div style={{ width: "100%" }}>
        <Row>
          <Col span={12}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() =>
                setOpenOrgModel({
                  open: true,
                  id: "",
                })
              }
            >
              Organisation
            </Button>
          </Col>
          <Col span={12}>
            <Row gutter={8}>
              <Col
                style={{
                  width: "100%",
                  textAlign: "right",
                }}
              >
                <Input
                  value={searchText}
                  allowClear
                  style={{ width: "200px", border: "1px solid #f1f3ff" }}
                  placeholder="Search.."
                  onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchText(val?.target?.value)
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row style={{ width: "100%" }}>
          <Col span={24}>
            <Table
              loading={dataLoading}
              className={`Tabel-style table-${
                themeData?.componentSize ?? "middle"
              }`}
              style={{ width: "100%", marginTop: "10px" }}
              columns={orgCols}
              dataSource={orgList}
            ></Table>
          </Col>
        </Row>
      </div>
      {openOrgModel && (
        <AddOrganisationModel
          isModalOpen={openOrgModel.open}
          setIsModalOpen={setOpenOrgModel}
          id={openOrgModel.id}
          setTrigger={setAddUpdateTrigger}
        ></AddOrganisationModel>
      )}
      {deleteConfirmation && (
        <ConfirmationModal
          open={deleteConfirmation.open}
          onNo={() =>
            setDeleteConfirmation({
              open: false,
              id: "",
            })
          }
          onYes={() => handleDelete(deleteConfirmation?.id)}
          text={`Are you sure you want to delete this row?`}
        />
      )}
    </Card>
  );
};

export default Organisations;
